import {API_BASE_URL} from '../utils/constants';

// Safely converts any FastAPI error detail value to a readable string.
// Handles: string, Pydantic v2 validation array [{loc, msg}], plain object, null/undefined.
const _detailToString = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        try { return detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; '); }
        catch (_) { return JSON.stringify(detail); }
    }
    try { return JSON.stringify(detail); } catch (_) { return fallback; }
};

// API service for student dashboard
const apiService = {
    // Get all teachers
    getTeachers: async () => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/teachers`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to fetch teachers"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching teachers:", error);
            throw error;
        }
    },

    // Get all classes with enrollment status
    getClasses: async () => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to fetch classes"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching classes:", error);
            throw error;
        }
    },

    // Enroll in a class
    enrollInClass: async (classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes/${classId}/enroll`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to enroll in class"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error enrolling in class:", error);
            throw error;
        }
    },

    // Drop a class
    dropClass: async (classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes/${classId}/enroll`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to drop class"));
            }

            return "Successfully dropped class";
        } catch (error) {
            console.error("Error dropping class:", error);
            throw error;
        }
    },

    // Get classmates in a class
    getClassmates: async (classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes/${classId}/classmates`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to fetch classmates"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching classmates:", error);
            throw error;
        }
    },

    // Get all assignments
    getAssignments: async () => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/assignments`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to fetch assignments"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching assignments:", error);
            throw error;
        }
    },

    // Get class assignments
    getClassAssignments: async (classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes/${classId}/assignments`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to fetch class assignments"));
            }

            const data = await response.json();
            // Ensure competencies field exists to prevent slice errors
            if (Array.isArray(data)) {
                return data.map(assignment => ({
                    ...assignment,
                    competencies: assignment.competencies || []
                }));
            }
            return data;
        } catch (error) {
            console.error("Error fetching class assignments:", error);
            throw error;
        }
    },

    // Get assignment details - expects StudentDomainAssignment response
    getAssignment: async (assignmentId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                let errorDetail = "Failed to fetch assignment details";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    // If response is not JSON, use status text
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching assignment details:", error);
            throw error;
        }
    },

    // // UPDATED: Unified submit assignment method (handles both file and text submissions)
    // submitAssignment: async (assignmentId, dataOrFile, isTextSubmission = false) => {
    //     try {
    //         const accessToken = localStorage.getItem("accessToken");
    //
    //         console.log('API Service - Submitting:', {
    //             assignmentId,
    //             isTextSubmission,
    //             dataType: isTextSubmission ? 'text' : 'file',
    //             dataLength: isTextSubmission ? dataOrFile?.length : 'N/A',
    //             fileName: !isTextSubmission ? dataOrFile?.name : 'N/A'
    //         });
    //
    //         if (isTextSubmission) {
    //             // Text submission - send as JSON body with embedded payload
    //             // The backend expects: Body(..., embed=True) which means the payload should be wrapped
    //             const payload = {
    //                 payload: dataOrFile  // Array of strings
    //             };
    //
    //             console.log('Sending text payload:', payload);
    //
    //             const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}/submit`, {
    //                 method: "POST",
    //                 headers: {
    //                     "Authorization": `Bearer ${accessToken}`,
    //                     "Content-Type": "application/json"
    //                 },
    //                 body: JSON.stringify(payload)
    //             });
    //
    //             if (!response.ok) {
    //                 let errorDetail = "Failed to submit answers";
    //                 try {
    //                     const errorData = await response.json();
    //                     errorDetail = _detailToString(errorData.detail, errorDetail);
    //                     console.error('Text submission error response:', errorData);
    //                 } catch (e) {
    //                     errorDetail = response.statusText || errorDetail;
    //                 }
    //                 throw new Error(errorDetail);
    //             }
    //
    //             return await response.json();
    //
    //         } else {
    //             // File submission - send as FormData (no JSON payload at all)
    //             if (!dataOrFile || !(dataOrFile instanceof File)) {
    //                 throw new Error("Invalid file provided for upload");
    //             }
    //
    //             const formData = new FormData();
    //             formData.append("file", dataOrFile);
    //
    //             console.log('Sending file:', {
    //                 fileName: dataOrFile.name,
    //                 fileSize: dataOrFile.size,
    //                 fileType: dataOrFile.type
    //             });
    //
    //             const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}/submit`, {
    //                 method: "POST",
    //                 headers: {
    //                     "Authorization": `Bearer ${accessToken}`
    //                     // Important: Don't set Content-Type for FormData - browser sets it with boundary
    //                 },
    //                 body: formData
    //             });
    //
    //             if (!response.ok) {
    //                 let errorDetail = "Failed to submit file";
    //                 try {
    //                     const errorData = await response.json();
    //                     errorDetail = _detailToString(errorData.detail, errorDetail);
    //                     console.error('File submission error response:', errorData);
    //                 } catch (e) {
    //                     errorDetail = response.statusText || errorDetail;
    //                 }
    //                 throw new Error(errorDetail);
    //             }
    //
    //             return await response.json();
    //         }
    //
    //     } catch (error) {
    //         console.error(`Error submitting ${isTextSubmission ? 'answers' : 'file'}:`, error);
    //         throw error;
    //     }
    // },


    // apiService.js
// ---------------------------------------------------------------------------
// Unified submitAssignment – works for both *text* and *file* submissions
// ---------------------------------------------------------------------------

    submitAssignment: async (assignmentId, dataOrFile, isTextSubmission = false) => {
        const accessToken = localStorage.getItem("accessToken");

        try {
            /* -------------------------------------------------------------
               1. Build the multipart/form‑data body
            ------------------------------------------------------------- */
            const formData = new FormData();

            if (isTextSubmission) {
                if (!Array.isArray(dataOrFile) || dataOrFile.length === 0) {
                    throw new Error("Text submission requires a non‑empty array of answers");
                }

                // FastAPI aggregates repeated keys into List[str]
                dataOrFile.forEach(ans => formData.append("answers", ans));

                console.log("[submitAssignment] sending TEXT answers", dataOrFile.length);
            } else {
                if (!(dataOrFile instanceof File)) {
                    throw new Error("File submission expects a File object");
                }

                formData.append("file", dataOrFile);
                console.log("[submitAssignment] sending FILE", {
                    name: dataOrFile.name,
                    size: dataOrFile.size,
                    type: dataOrFile.type,
                });
            }

            /* -------------------------------------------------------------
               2. Fire the request
            ------------------------------------------------------------- */
            const resp = await fetch(
                `${API_BASE_URL}/assignments/${assignmentId}/submit`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        // NO manual Content‑Type header – the browser sets the boundary
                    },
                    body: formData,
                },
            );

            if (!resp.ok) {
                // Try to surface backend “detail” messages
                let msg = resp.statusText || "Submission failed";
                try {
                    const errData = await resp.json();
                    if (errData.detail) msg = _detailToString(errData.detail, msg);
                } catch (_) { /* ignore JSON parse errors */
                }
                throw new Error(msg);
            }

            return await resp.json();
        } catch (err) {
            console.error("[submitAssignment] error:", err);
            throw err;   // let callers decide how to surface it
        }
    },

    // REMOVED: submitAnswers method (now handled by unified submitAssignment)

    // Get submission - expects SubmissionDetail response
    getSubmission: async (assignmentId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}/submission`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                // If status is 404, we just have no submission yet
                if (response.status === 404) {
                    throw new Error("No submission found");
                }

                let errorDetail = "Failed to fetch submission";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    // If response is not JSON, use status text
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching submission:", error);
            throw error;
        }
    },

    // Add self-assessment
    addSelfAssessment: async (assignmentId, selfAssessmentData) => {
        try {
            const accessToken = localStorage.getItem("accessToken");

            // Matches new EnhancedSelfAssessment model
            const payload = {
                liked: selfAssessmentData.liked,
                easy: selfAssessmentData.easy,
                needed: selfAssessmentData.needed
            };

            const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}/self-assessment`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to add self-assessment"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding self-assessment:", error);
            throw error;
        }
    },

    // Add peer-assessment
    addPeerAssessment: async (assignmentId, peerAssessmentData) => {
        try {
            const accessToken = localStorage.getItem("accessToken");

            // Matches new PeerAssessment model
            const payload = {
                friend_liked: peerAssessmentData.friend_liked,
                friend_easy: peerAssessmentData.friend_easy,
                friend_needed: peerAssessmentData.friend_needed
            };

            const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}/peer-assessment`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(_detailToString(errorData.detail, "Failed to add peer-assessment"));
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding peer-assessment:", error);
            throw error;
        }
    },

    // Get class attendance records
    getAttendanceRecords: async (classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes/${classId}/attendance`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                let errorDetail = "Failed to fetch attendance records";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    // If response is not JSON, use status text
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching attendance records:", error);
            throw error;
        }
    },

    // Get attendance summary
    getAttendanceSummary: async (classId) => {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/classes/${classId}/attendance/summary`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                let errorDetail = "Failed to fetch attendance summary";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    // If response is not JSON, use status text
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching attendance summary:", error);
            throw error;
        }
    }
};

export default apiService;